import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { IndicatorCodes } from 'src/app/core/_enums/indicators.code';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { UploadFileName } from 'src/app/core/_enums/upload.file.name';
import { GlobalConstants } from 'src/constants/global.constants';

import { CeuValue } from 'src/app/core/_models/ceuValues';
import { Energie } from 'src/app/core/_models/energie';
import { Indicator } from 'src/app/core/_models/indicator';
import { Operation } from 'src/app/core/_models/operation';
import { OperationType } from 'src/app/core/_models/operationType';
import { ProjectType } from 'src/app/core/_models/projectType';
import { Scenario } from 'src/app/core/_models/scenario';
import { Solution } from 'src/app/core/_models/solution';
import { Transport } from 'src/app/core/_models/transport';
import { WorkInspectionModel } from 'src/app/core/_models/workInspection';

import { AuthService } from 'src/app/core/_services/auth.service';
import { EnergieService } from 'src/app/core/_services/energie.service';
import { OperationService } from 'src/app/core/_services/operation.service';
import { ProjectService } from 'src/app/core/_services/project.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SolutionService } from 'src/app/core/_services/solution.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TransportService } from 'src/app/core/_services/transport.service';
import { WorkInspectionService } from 'src/app/core/_services/workInspection.service';
import { AppState } from 'src/app/store/app.state';
import { environment } from 'src/environments/environment';

import { FunctionalityCode } from 'src/app/core/_enums/functionality.code';
import { Version } from 'src/app/core/_models/version';
import { VersionService } from 'src/app/core/_services/versions.service';
import { UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmCancelDialogComponent } from '../shared/confirm-cancel-dialog/confirm-cancel-dialog.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';
import { SyntheseButtonsConfig } from '../synthese/synthese.component';

@Component({
  selector: 'app-work-inspection',
  templateUrl: './work-inspection.component.html',
  styleUrls: ['./work-inspection.component.scss']
})
export class WorkInspectionComponent implements OnInit {
  SiteMapUrl = SiteMapUrl;
  UploadFileName = UploadFileName;

  @ViewChild('tabs') tabGroup!: MatTabGroup;
  @ViewChild('fileWaterInput', { static: false })
  fileWaterInput!: ElementRef;

  @ViewChild('fileBioInput', { static: false })
  fileBioInput!: ElementRef;

  @ViewChildren('panelRef') panelRefs!: QueryList<any>;
  @ViewChildren('typeSelect') typeSelects!: QueryList<ElementRef>;

  form!: FormGroup;
  currentItem!: WorkInspectionModel;
  scenario?: Scenario;
  perimeterId!: string;

  originalSolution?: Solution;
  verificationSolution?: Solution;

  operationTypes!: OperationType[];

  indicators: Indicator[] = [];
  indexEnergyToShow: number = 0;
  totalCostOriginal: number = 0;
  totalCostVerification: number = 0;
  selected = new FormControl(0);

  showTopBtns: boolean = true;
  unSaved: boolean = false;
  wsAppPath: string;
  scenarioTooltipMessage: string = '';
  tabSelectedIndex = 0;
  isScenarioTabActivated = false;
  scenarioLoading = false;
  readonly scenarioTabIndex = 2;
  scenarioRefreshKey = 0;

  biodiversityList!: any[];
  waterList!: any[];
  filtredTypes!: ProjectType[];
  inputProductsList!: Transport[];
  energies!: Energie[];
  showIncompleteProductsWarning: boolean = false;
  incompleteProductsList: string[] = [];
  totalCost: number = 0;

  isLoadingProducts: boolean = false;
  isEditable: boolean = false;
  isExportable: boolean = false;

  dbVersions: Version[] = [];

  syntheseButtonsConfig: SyntheseButtonsConfig = {
    generatePdf: { visibility: 'hidden' },
    extractExcel: { visibility: 'visible', callback: () => this.exportAsExcel() },
    checkData: { visibility: 'hidden' }
  };

  constructor(
    private workInspectionService: WorkInspectionService,
    private projectService: ProjectService,
    private solutionService: SolutionService,
    private operationService: OperationService,
    private transportService: TransportService,
    private energieService: EnergieService,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private matDialog: MatDialog,
    private snackbarService: SnackbarService,
    private authService: AuthService,
    private store: Store<AppState>,
    private spinnerService: SpinnerOverlayService,
    private versionService: VersionService,
    private translate: TranslateService
  ) {
    this.wsAppPath = environment.baseUrl;
    this.scenarioTooltipMessage = this.translate.instant('work_inspection_no_scenario_available');
  }

  ngOnInit(): void {
    const perimeterId = this.authService.roleSubject.value.perimeterId;
    this.perimeterId = perimeterId;

    if (perimeterId) {
      this.transportService.getAll().subscribe((data) => (this.inputProductsList = data));
      this.energieService.getAllForTransportation().subscribe((data) => (this.energies = data));
    }

    this.operationService.getOperationTypes().subscribe((types) => (this.operationTypes = types));
    this.solutionService.getBiodiversityList().subscribe((list) => (this.biodiversityList = list));
    this.solutionService.getWaterList().subscribe((list) => (this.waterList = list));

    this.projectService.getIndicators().subscribe((arr: Indicator[]) => {
      this.indicators = arr
        .filter((row: any) => {
          return (
            row.code === IndicatorCodes.Energie ||
            row.code === IndicatorCodes.CO2 ||
            row.code === IndicatorCodes.ReclaimedAsphalt ||
            row.code === IndicatorCodes.NaturalAggregaes ||
            row.code === IndicatorCodes.SoildReused ||
            row.code === IndicatorCodes.RecycledMaterials ||
            row.code === IndicatorCodes.TonneKilometre
          );
        })
        .sort((a, b) => {
          if (a.code === IndicatorCodes.CO2) return -1;
          if (b.code === IndicatorCodes.CO2) return 1;
          return 0;
        });
    });

    this.versionService.getAll().subscribe((versions) => {
      this.dbVersions = versions;
    });

    this.activeRoute.params.subscribe((params) => {
      const id = params['id'];

      if (id && id !== 'new' && id !== '0') {
        this.loadWorkInspection(id);
      } else {
        this.snackbarService.open(SnackbarTypes.error, this.translate.instant('work_inspection_select_solution_required'));
        this.router.navigate([SiteMapUrl.MenuWorkInspection]);
      }
    });
    this.selected.setValue(0);
  }

  ngDoCheck(): void {
    const accesses = this.authService?.accessesValues ?? [];
    this.isEditable = accesses.includes(FunctionalityCode.ManageWorkInspection);
    this.isExportable = accesses.includes(FunctionalityCode.ImportProject);
  }

  onSolutionUpdated(updatedSolution: Solution): void {
    this.verificationSolution = updatedSolution;
    this.calculateTotals();
  }

  // Placebo as there is autosave on workInspections
  saveWorkInspection(): void {
    this.spinnerService.show();
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    sleep(1000).then(() => {
      this.spinnerService.hide();
      this.snackbarService.open(SnackbarTypes.add);
    });
  }

  exportAsSevetp(): void {
    if (!this.currentItem?.workInspectionId) return;

    const dialogRef = this.matDialog.open(ConfirmCancelDialogComponent, {
      data: {
        message: this.translate.instant('work_inspection_confirm_export'),
        confirmText: this.translate.instant('action_bar_export'),
        confirmColor: 'accent'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.spinnerService.show();
      this.workInspectionService.exportWorkInspectionFile(this.currentItem.workInspectionId).subscribe({
        next: (blob) => {
          const nowDate = new Date();
          const fileName = `${this.currentItem.displayName ?? 'suivi'}-${nowDate.getDate().toString().padStart(2, '0')}-${nowDate.getMonth().toString().padStart(2, '0')}-${nowDate.getFullYear()}`;
          this.downloadWithLegacyMethod(blob, fileName);
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('SEVETP Export error:', error);
          this.snackbarService.open(SnackbarTypes.error);
          this.spinnerService.hide();
        }
      });
    });
  }

  exportAsExcel(): void {
    if (!this.currentItem?.workInspectionId) return;

    this.spinnerService.show();
    this.workInspectionService.exportWorkInspectionExcel(this.currentItem.workInspectionId).subscribe({
      next: (blob) => {
        this.downloadFile(blob, `${this.currentItem.displayName ?? 'suivi'}.xlsx`);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Excel Export error:', error);
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    });
  }

  majStore(snackbarMsgType: string) {
    this.workInspectionService.getAll().subscribe(
      (arr) => {
        if (!arr) {
          return;
        }
        this.store.dispatch(new UpdateResourceAction(arr));
        this.snackbarService.open(snackbarMsgType);
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );
  }

  loadWorkInspection(id: string): void {
    this.workInspectionService.getById(id).subscribe(
      (workInspection: WorkInspectionModel) => {
        if (workInspection.versionId && this.dbVersions.length > 0) {
          const version = this.dbVersions.find((v) => v.versionId === workInspection.versionId);
          if (version) {
            workInspection.versionName = version.versionName;
          }
        }
        this.currentItem = workInspection;
        this.resetTabs();

        this.originalSolution = workInspection.originSolution ? this._mapPeriodsToOperations(workInspection.originSolution) : undefined;
        this.verificationSolution = workInspection.inspectionSolution ? this._mapPeriodsToOperations(workInspection.inspectionSolution) : undefined;

        if (workInspection.inspectionSolutionId && !workInspection.inspectionSolution) {
          this.snackbarService.showMessage(this.translate.instant('work_inspection_inspection_solution_missing'));
        }

        this.calculateTotals();
      },
      () => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );
  }

  private loadScenarioIfNeeded(): void {
    const scenarioId = this.currentItem?.scenarioId;
    if (!scenarioId || !this.isValidScenarioId(scenarioId) || this.scenarioLoading || this.scenario?.scenarioId === scenarioId) {
      return;
    }
    this.fetchScenario(scenarioId);
  }

  private fetchScenario(scenarioId: string): void {
    this.scenarioLoading = true;
    this.projectService.getScenarioById(scenarioId).subscribe({
      next: (scenario) => (this.scenario = scenario),
      error: () => (this.scenario = undefined),
      complete: () => {
        this.scenarioLoading = false;
      }
    });
  }

  private isValidScenarioId(id?: string): boolean {
    return !!id && id !== GlobalConstants.EMPTY_GUID;
  }

  private resetTabs(): void {
    this.tabSelectedIndex = 0;
    this.isScenarioTabActivated = false;
    this.selected.setValue(0);
    this.scenario = undefined;
    this.scenarioLoading = false;
  }

  get hasValidScenarioId(): boolean {
    return this.isValidScenarioId(this.currentItem?.scenarioId);
  }

  private _mapPeriodsToOperations(solution: Solution): Solution {
    if (!solution.operations || !solution.solutionTemporalities) {
      return solution;
    }
    const operationMap = new Map<string, Operation>();
    solution.operations.forEach((op) => {
      op.periodId = undefined;
      if (op.id) {
        operationMap.set(op.id, op);
      }
    });

    solution.solutionTemporalities.forEach((period) => {
      if (period.listOperation) {
        period.listOperation.forEach((operationId) => {
          const operation = operationMap.get(operationId);
          // Une opération n'est présente que dans une période, ses autres présences sont des copies.
          // Un double rattachement est une anomalie de données : la première période l'emporte,
          // plutôt que la dernière lue.
          if (operation && !operation.periodId) {
            operation.periodId = period.temporalityId;
          }
        });
      }
    });

    return solution;
  }

  calculateTotals(): void {
    this.totalCostOriginal = this.sumSolutionCost(this.originalSolution);
    this.totalCostVerification = this.sumSolutionCost(this.verificationSolution);
  }

  private sumSolutionCost(solution?: Solution): number {
    return solution?.operations?.reduce((sum, operation) => sum + this.getValue(operation), 0) ?? 0;
  }

  getValue(operation: Operation): number {
    const indicator = this.indicators[this.indexEnergyToShow];
    if (!indicator) {
      return 0;
    }

    return operation.detailedOperation?.ceuValues?.find((x: CeuValue) => x.code === indicator.code && x.subIndicatorCode == 'TotalCost')?.ceuValue ?? 0;
  }

  changeTotalToShow(): void {
    this.calculateTotals();
  }

  onSelectedTabChange(event: MatTabChangeEvent): void {
    this.tabSelectedIndex = event.index;
    const isScenarioTab = event.index === this.scenarioTabIndex;
    if (isScenarioTab && this.hasValidScenarioId) {
      this.isScenarioTabActivated = true;
      this.loadScenarioIfNeeded();
      this.scenarioRefreshKey++;
    }
  }

  deleteWorkInspection() {
    if (!this.currentItem?.workInspectionId) return;

    const dialogRef = this.matDialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('work_inspection_confirm_delete') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.workInspectionService.delete(this.currentItem!.workInspectionId!).subscribe({
          next: () => {
            this.majStore(SnackbarTypes.delete);
            this.workInspectionService.refreshDataSource();
            this.router.navigate([SiteMapUrl.MenuWorkInspection]);
          },
          error: (error) => {
            this.snackbarService.open(SnackbarTypes.error);
            console.error(error);
          }
        });
      }
    });
  }

  private downloadWithLegacyMethod(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.slugify(fileName) + '.sevetp';
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  private slugify(text: string): string {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }

  private downloadFile(data: Blob, fileName: string): void {
    const contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const blob = new Blob([data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(url);
  }
}
